import { useState } from 'react';
import { Box, Popover, Stack } from '@mui/material';
import { Button, colors, Typography, IconButton, Tag } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';
import { selectAgents } from '../../../aiAgentCenter/agentsSelectors';
import { AgentType, MonitoringV2ContextComponent } from '@server/ai';
import useObjectTypesWithFetch from '../../../../hooks/useObjectTypesWithFetch';
import { AiAgentWithFacts } from '../../../aiAgentCenter/aiAgentsConsts';
import pluralize from 'pluralize';
import { getTotalIssues } from './getTotalIssues';
import { CreateMonitoringAgentDialogWrapper } from '../../../aiAgentCenter/CreateMonitoringAgentDialogWrapper';
import { AgentPopoverCard } from './AgentPopoverCard';
import { MonitoringAgentsPopoverEmptyState } from './MonitoringAgentsPopoverEmptyState';

interface MonitoringAgentsPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const MonitoringAgentsPopover = ({ open, anchorEl, onClose }: MonitoringAgentsPopoverProps) => {
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const agents = useSelector(selectAgents(crmOrgId ?? undefined));
  const { objectTypesByName } = useObjectTypesWithFetch({ crmOrgId: crmOrgId ?? '' });
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const monitoringAgents = agents?.filter((agent) => agent.type === AgentType.MonitoringV2) ?? [];

  const getAgentDetails = (agent: AiAgentWithFacts) => {
    const objectType = (agent.contextComponents as MonitoringV2ContextComponent)?.objectType;
    const totalIssues = getTotalIssues([agent]);

    let objectLabel = 'All Objects';
    if (objectType) {
      objectLabel = objectTypesByName[objectType]?.label ?? objectType;
    }

    return {
      objectLabel,
      totalIssues,
    };
  };

  const totalIssues = getTotalIssues(monitoringAgents);

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              border: `1px solid ${colors.grey[300]}`,
              width: '760px',
              maxHeight: '500px',
              mt: '-5px',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              borderBottom: `1px solid ${colors.grey[300]}`,
            }}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="h4">Monitoring</Typography>
                {monitoringAgents.length > 0 && (
                  <Typography variant="caption" color={colors.grey[700]}>
                    {pluralize('Agent', monitoringAgents.length, true)}
                  </Typography>
                )}
              </Stack>
              <Tag
                size="small"
                color={colors.seafoam[300]}
                label={
                  monitoringAgents.length > 0
                    ? pluralize('issue', totalIssues, true)
                    : 'Resolve Tech Debt'
                }
              />
            </Stack>
            <IconButton onClick={onClose} variant="flat" size="tiny">
              <Close />
            </IconButton>
          </Box>

          <Box
            sx={{
              p: 2,
              overflow: 'auto',
              maxHeight: '213px',
              height: '100%',
            }}
          >
            {monitoringAgents.length === 0 ? (
              <MonitoringAgentsPopoverEmptyState crmOrgId={crmOrgId ?? ''} onClose={onClose} />
            ) : (
              monitoringAgents.map((agent, index) => {
                const { objectLabel, totalIssues: agentIssues } = getAgentDetails(agent);
                const isLast = index === monitoringAgents.length - 1;

                return (
                  <AgentPopoverCard
                    key={agent.id}
                    agent={agent}
                    objectLabel={objectLabel}
                    agentIssues={agentIssues}
                    isLast={isLast}
                  />
                );
              })
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              p: 3,
              pt: 2,
              pb: 2,
            }}
          >
            <Button
              variant="system"
              size="small"
              onClick={() => {
                setShowCreateDialog(true);
                onClose();
              }}
            >
              Create New Agent
            </Button>
          </Box>
        </Box>
      </Popover>

      {showCreateDialog && (
        <CreateMonitoringAgentDialogWrapper
          setIsNewMonitoringAgentDialogOpened={setShowCreateDialog}
          isNewMonitoringAgentDialogOpened={showCreateDialog}
          crmOrgId={crmOrgId ?? ''}
        />
      )}
    </>
  );
};

export default MonitoringAgentsPopover;
